<script lang="ts" setup>
import type { SidebarNavItem } from '~kui/components/KUI/Schools/Layout/Sidebar/types';
import type { School } from '~kui/components/KUI/Schools/Switcher/types';

import { ICONS } from '~ds/lib/icons';

import { useSchoolsFeedbackModal } from '~/composables/useSchoolsFeedbackModal';

interface Props {
  loading?: boolean;
}

withDefaults(defineProps<Props>(), {
  loading: false,
});

const { t } = useI18n();
const route = useRoute();
const {
  user,
  logout,
  isLoggedIn,
  currentSchoolId,
  currentSchool,
  schoolOperationalRoles,
} = useAuth();

const {
  isOpen: isFeedbackOpen,
  submitting: feedbackSubmitting,
  openFeedback,
  submitFeedback,
} = useSchoolsFeedbackModal();

const schools = computed<School[]>(() =>
  schoolOperationalRoles.value
    .filter(role => role.school?.id && role.school?.name)
    .map(role => ({
      id: role.school!.id,
      name: role.school!.name,
    })),
);

const selectedSchoolId = computed(() => currentSchoolId.value ?? schools.value[0]?.id);

const selectedSchoolName = computed(
  () => currentSchool.value?.name
    ?? schools.value.find(s => s.id === selectedSchoolId.value)?.name,
);

const sidebarNavItems = computed<SidebarNavItem[]>(() => {
  if (!isLoggedIn.value) {
    return [];
  }

  const items: (SidebarNavItem & { hide?: boolean })[] = [
    {
      to: '/educators/lessons',
      label: t('schools.nav.lessons'),
      iconPath: ICONS.bookOpen,
    },
    {
      to: '/educators/students',
      label: t('schools.nav.students'),
      iconPath: ICONS.userGroup,
    },
  ];

  return items
    .filter(item => !item.hide)
    .map(({ hide: _hide, ...item }) => ({
      ...item,
      isActive: !!item.to && !item.target
        && (route.path === item.to || route.path.startsWith(`${item.to}/`)),
    }));
});
</script>

<template>
  <div>
    <LayoutSchoolsLessonsHub
      :nav-items="sidebarNavItems"
      :loading="loading"
      logo-href="/educators/lessons"
      account-href="/educators/account"
      :school-name="selectedSchoolName"
      @logout="logout"
    >
      <template v-if="selectedSchoolName" #school>
        <SchoolsAccountSwitcher
          :current-school-name="selectedSchoolName"
          :current-is-personal="currentSchool?.isPersonalSchool ?? false"
          :accounts="user?.switchableAccounts ?? []"
        />
      </template>
      <template #promo>
        <SchoolsFeedbackCTA @click="openFeedback" />
      </template>
      <template v-if="$slots.cardContent" #cardContent>
        <slot name="cardContent" />
      </template>
      <template v-if="$slots.plainContent" #plainContent>
        <slot name="plainContent" />
      </template>
      <slot />
    </LayoutSchoolsLessonsHub>
    <SchoolsFeedbackModal
      v-model:open="isFeedbackOpen"
      :submitting="feedbackSubmitting"
      @submit="submitFeedback"
    />
    <CoreSessionTimeoutModal />
  </div>
</template>
